import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Link } from '@mui/material';
import { RhNumberField } from '../../../../../../../dum';
import { ITransfer } from '../../transfer-form';
import {
    AMOUNT,
    EXPENSE,
    INITIAL_EXPENSE_ID,
    INITIAL_ORDER_SALE_ID,
    ORDER_SALE,
    SUGGESTED_AMOUNT,
    TRANSFER_RECEIPTS,
} from '../transfer-receipt-columns';
import { useCalculateTransferReceiptsTotal } from '../../use-calculate-transfer-receipt-total';
import { CURRENCY_INPUT_PROPS } from '../../currency-input-props';
import {
    FIELD_LABELS,
    formatFloat,
    round,
    shortLabel,
} from '../../../../../../../../helpers';

interface IProps {
    index: number;
}

// The amount a receipt allocates is intentionally NOT auto-overwritten when the
// linked document changes while editing an existing transfer — the user usually
// just wants to re-point the receipt and keep the figure. Instead the document's
// remaining balance is surfaced here as `suggested_amount`, with a one-click
// "Aplicar" link for when they do want it. On a brand-new transfer the document
// inputs still pre-fill the amount directly, so this hint is purely additive.
export function TransferAmountInput({ index }: IProps) {
    const rx = useFormContext();
    const { calculateTransferReceiptsTotal } =
        useCalculateTransferReceiptsTotal();

    const suggested = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${SUGGESTED_AMOUNT}`,
    }) as ITransfer['transfer_receipts'][number]['suggested_amount'];

    const amount = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`,
    }) as ITransfer['transfer_receipts'][number]['amount'];

    const orderSale = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${ORDER_SALE}`,
    }) as ITransfer['transfer_receipts'][number]['order_sale'];

    const expense = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${EXPENSE}`,
    }) as ITransfer['transfer_receipts'][number]['expense'];

    const initialOrderSaleId = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${INITIAL_ORDER_SALE_ID}`,
    }) as ITransfer['transfer_receipts'][number]['initial_order_sale_id'];

    const initialExpenseId = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${INITIAL_EXPENSE_ID}`,
    }) as ITransfer['transfer_receipts'][number]['initial_expense_id'];

    // The receipt is sitting on the same document it was loaded with — the
    // amount already reflects the original figure, so offering "apply the
    // balance" here would just be noise. Suppress the hint entirely in that case.
    const onOriginalDocument =
        (initialOrderSaleId != null && orderSale?.id === initialOrderSaleId) ||
        (initialExpenseId != null && expense?.id === initialExpenseId);

    const hasSuggestion =
        suggested !== null && suggested !== undefined && !onOriginalDocument;
    const matchesSuggestion =
        hasSuggestion && round(Number(amount)) === round(Number(suggested));

    const applySuggested = () => {
        rx.setValue(`${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`, suggested, {
            shouldValidate: true,
        });
        calculateTransferReceiptsTotal({ index });
    };

    return (
        <RhNumberField
            name={`${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`}
            label={shortLabel(FIELD_LABELS.amount)}
            InputProps={CURRENCY_INPUT_PROPS}
            onChange={() => {
                calculateTransferReceiptsTotal({ index });
            }}
            helperText={
                hasSuggestion ? (
                    <>
                        {`Saldo del documento: $${formatFloat(
                            Number(suggested),
                        )}`}
                        {!matchesSuggestion && (
                            <Link
                                component={'button'}
                                type={'button'}
                                onClick={applySuggested}
                                sx={{ ml: 1 }}
                            >
                                Aplicar
                            </Link>
                        )}
                    </>
                ) : undefined
            }
        />
    );
}
